import { useCallback, useEffect } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { useSidebarState } from "@/hooks/use-sidebar-state";
import { useRealtimeUpdates } from "@/hooks/use-realtime-updates";
import { useChatNotifications } from "@/hooks/use-chat-notifications";
import Sidebar from "@/components/layout/sidebar";
import Header from "@/components/layout/header";
import { cn } from "@/lib/utils";
import { GlobalChatButton } from "@/components/chat/global-chat-button";
import { FirmBillingBanners } from "@/components/billing/firm-billing-banners";
import { FeedbackButton } from "@/components/feedback/feedback-button";
import { MobileFabProvider } from "@/components/layout/mobile-fab";

interface Breadcrumb {
  label: string;
  href?: string;
  current?: boolean;
}

interface LayoutProps {
  children: React.ReactNode;
  breadcrumbs?: Breadcrumb[];
}

export default function Layout({ children, breadcrumbs = [] }: LayoutProps) {
  const [location] = useLocation();
  const isChatPage = location === "/chat" || location.startsWith("/chat/agent");
  const { isMobileOpen, setIsMobileOpen, isCollapsed, setIsCollapsed } = useSidebarState();
  const { toast } = useToast();
  const { isAuthenticated, isLoading, user } = useAuth();

  // Establish real-time WebSocket connection (with polling fallback)
  // for email, chat, and activity notifications across the entire app.
  // Memoized so the hook does not reconnect on every Layout render.
  const handleNotification = useCallback(
    (data: { title?: string; message?: string; notification?: { title?: string; message?: string } }) => {
      const notification = data.notification ?? data;
      toast({
        title: notification.title ?? 'New notification',
        description: notification.message ?? undefined,
      });
    },
    [toast],
  );
  useRealtimeUpdates({ onNotification: handleNotification });

  useChatNotifications();

  // Authentication check with useEffect to avoid render side-effects
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      toast({
        title: "Unauthorized",
        description: "You are logged out. Logging in again...",
        variant: "destructive",
      });
      setTimeout(() => {
        window.location.href = "/api/login";
      }, 500);
    }
  }, [isAuthenticated, isLoading, toast]);

  if (isLoading || !isAuthenticated) {
    return null;
  }

  return (
    // Below lg, Chat / Activity / Bugs & Features share one FAB. /chat has its own FAB stack, so it opts out.
    <MobileFabProvider enabled={!isChatPage}>
      <div className="min-h-screen bg-background">
        <Sidebar
          isMobileOpen={isMobileOpen}
          setIsMobileOpen={setIsMobileOpen}
          isCollapsed={isCollapsed}
          setIsCollapsed={setIsCollapsed}
        />

        {/* Main content area */}
        <div className={cn(
          "relative min-h-screen",
          isCollapsed ? "lg:pl-20" : "lg:pl-64"
        )}>
          <Header
            breadcrumbs={breadcrumbs}
            onMenuClick={() => setIsMobileOpen(true)}
          />

          <main className="p-3 sm:p-4 lg:p-6">
            <FirmBillingBanners />
            {children}
          </main>
        </div>
        {!isChatPage && <GlobalChatButton />}
        {/* Hides itself while the board's flag is off. */}
        <FeedbackButton />
      </div>
    </MobileFabProvider>
  );
}
